var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { PaneMenu, Button, MultiColumnList, Icon, RadioButton, RadioButtonGroup, Checkbox, Accordion, AccordionSet, Tooltip, Layout, Headline, Loading, Selection, MessageBanner, } from "@folio/stripes/components";
import SearchLayout from "../components/SearchLayout";
import UserDetailPane from "../components/UserDetailPane";
import StatusBadge from "../components/StatusBadge";
import useUserList from "../hooks/useUserList";
import useNavigation from "../hooks/useNavigation";
import useAuthorityTypes from "../hooks/useAuthorityTypes";
import { usePermissions } from "../utils/permissionsUtils";
import { fetchAllOrganizations } from "../api/organizationService";
import { buildOrganizationFilterOptions, filterOrganizationOptions, formatOrganizationLabel, } from "../utils/organizationOptions";
import { USER_LIST_ROLE_FILTERS, nkRoleMessageId, roleFilterMessageId, standardNkRoles, } from "../utils/nkRoles";
import { authorityTypeLabel, splitAuthorityTypes, withAssignedTypes, } from "../utils/authorityTypes";
/**
 * Every organization, archived ones included — a system administrator looks for the people of a
 * library regardless of its state (CZNA-1588). Sorted the way the column sorts.
 */
var ORGANIZATION_FILTER_QUERY = "cql.allRecords=1 sortBy sigla/sort.ascending name/sort.ascending";
var UsersList = function (_a) {
    var match = _a.match, selectedId = _a.selectedId;
    var intl = useIntl();
    var _b = useNavigation(match.path), goToList = _b.goToList, goToDetail = _b.goToDetail, goToCreate = _b.goToCreate, goToEdit = _b.goToEdit;
    var canCreateUser = usePermissions().canCreateUser;
    var list = useUserList();
    var ky = useOkapiKy();
    var _c = useQuery(["czna-organizations", "user-filter"], function () {
        return fetchAllOrganizations(ky, ORGANIZATION_FILTER_QUERY);
    }), organizations = _c.data, organizationsLoading = _c.isLoading, organizationsLoaded = _c.isSuccess, organizationsError = _c.isError;
    var organizationOptions = useMemo(function () {
        return buildOrganizationFilterOptions(organizations !== null && organizations !== void 0 ? organizations : [], intl, list.organizationFilter, organizationsLoaded);
    }, [organizations, intl, list.organizationFilter, organizationsLoaded]);
    // CZNA-1610 — the authority types the NK roles narrow to: the registry the user form offers,
    // in the form's order and labels. A type in the filter that the registry does not name (a
    // removed type, a typo in a shared link, or any type while the registry cannot be loaded)
    // is listed too, by its raw code: it stays in the filter and empties the NK branch, and
    // the admin has to see why and be able to uncheck it. The applied types count as well, so
    // an unchecked one keeps its box until the search applies the change. Nothing is listed
    // while the registry is loading — every type would briefly show as a raw code.
    var _d = useAuthorityTypes(), authorityTypes = _d.authorityTypes, authorityTypesLoading = _d.isLoading, authorityTypesError = _d.isError;
    var authorityTypeOptions = useMemo(function () {
        if (authorityTypesLoading)
            return [];
        var _a = splitAuthorityTypes(intl, withAssignedTypes(authorityTypes !== null && authorityTypes !== void 0 ? authorityTypes : [], __spreadArray(__spreadArray([], list.authorityTypeFilter, true), list.appliedAuthorityTypeFilter, true))), recordTypes = _a.recordTypes, vocabularyTypes = _a.vocabularyTypes;
        return __spreadArray(__spreadArray([], recordTypes, true), vocabularyTypes, true);
    }, [
        authorityTypes,
        authorityTypesLoading,
        intl,
        list.authorityTypeFilter,
        list.appliedAuthorityTypeFilter,
    ]);
    var handleRowClick = useCallback(function (_e, row) {
        goToDetail(row.id);
    }, [goToDetail]);
    var resultsFormatter = {
        name: function (user) {
            var displayName = "".concat(user.lastName, ", ").concat(user.firstName);
            var MAX_NAME_LENGTH = 50;
            if (displayName.length <= MAX_NAME_LENGTH) {
                return displayName;
            }
            var truncated = "".concat(displayName.slice(0, MAX_NAME_LENGTH), "...");
            return (React.createElement(Tooltip, { text: displayName, id: "name-tooltip-".concat(user.id) }, function (_a) {
                var ref = _a.ref, ariaIds = _a.ariaIds;
                return (React.createElement("span", { ref: ref, "aria-labelledby": ariaIds.text }, truncated));
            }));
        },
        email: function (user) { return user.email; },
        organization: function (user) {
            return formatOrganizationLabel(user.organizationSigla, user.organizationName) ||
                "\u2014";
        },
        status: function (user) { return React.createElement(StatusBadge, { status: user.status }); },
        roles: function (user) {
            var _a;
            // CZNA-1598: the global roles, then the NK roles held on standard authority types.
            var translatedRoles = __spreadArray(__spreadArray([], ((_a = user.roles) !== null && _a !== void 0 ? _a : []).map(function (r) {
                return intl.formatMessage({ id: "ui-czna-users.role.".concat(r.role) });
            }), true), standardNkRoles(user).map(function (nkRole) {
                return intl.formatMessage({ id: nkRoleMessageId(nkRole) });
            }), true);
            if (translatedRoles.length === 0)
                return "\u2014";
            var MAX_VISIBLE = 3;
            if (translatedRoles.length <= MAX_VISIBLE) {
                return translatedRoles.join(", ");
            }
            var visible = translatedRoles.slice(0, MAX_VISIBLE).join(", ");
            var remaining = translatedRoles.length - MAX_VISIBLE;
            var allRolesText = translatedRoles.join(", ");
            return (React.createElement("span", null,
                visible,
                ", ",
                React.createElement(Tooltip, { text: allRolesText, id: "role-badge-".concat(user.id) }, function (_a) {
                    var ref = _a.ref, ariaIds = _a.ariaIds;
                    return (React.createElement("span", { ref: ref, "aria-labelledby": ariaIds.text, style: {
                            display: "inline-block",
                            padding: "0 6px",
                            borderRadius: "10px",
                            backgroundColor: "#e8e8e8",
                            fontSize: "0.85em",
                            cursor: "default",
                        } },
                        "+",
                        remaining));
                })));
        },
        createdAt: function (user) {
            if (!user.createdAt)
                return "";
            var date = intl.formatDate(user.createdAt, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            });
            var time = intl.formatTime(user.createdAt, {
                hour: "2-digit",
                minute: "2-digit",
            });
            return "".concat(date, " ").concat(time);
        },
    };
    var filterContent = (React.createElement(AccordionSet, null,
        React.createElement(Accordion, { label: intl.formatMessage({ id: "ui-czna-users.users.filter.status" }), separator: false },
            React.createElement(RadioButtonGroup, { value: list.statusFilter, onChange: function (e) {
                    return list.handleStatusFilterChange(e.target.value);
                } },
                React.createElement(RadioButton, { label: intl.formatMessage({
                        id: "ui-czna-users.users.filter.status.all",
                    }), value: "ALL" }),
                React.createElement(RadioButton, { label: intl.formatMessage({
                        id: "ui-czna-users.users.filter.status.active",
                    }), value: "ACTIVE" }),
                React.createElement(RadioButton, { label: intl.formatMessage({
                        id: "ui-czna-users.users.filter.status.inactive",
                    }), value: "INACTIVE" }))),
        React.createElement(Accordion, { label: intl.formatMessage({ id: "ui-czna-users.users.filter.role" }), separator: false }, USER_LIST_ROLE_FILTERS.map(function (role) { return (React.createElement(Checkbox, { key: role, label: intl.formatMessage({ id: roleFilterMessageId(role) }), checked: list.roleFilter.includes(role), onChange: function (e) {
                return list.handleRoleFilterChange(role, e.target.checked);
            } })); })),
        React.createElement(Accordion, { label: intl.formatMessage({
                id: "ui-czna-users.users.filter.authorityType",
            }), separator: false },
            authorityTypesError ? (React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-users.users.filter.authorityType.loadError" }))) : null,
            authorityTypesLoading ? React.createElement(Loading, null) : null,
            authorityTypeOptions.map(function (type) { return (React.createElement(Checkbox, { key: type.code, label: authorityTypeLabel(intl, type), checked: list.authorityTypeFilter.includes(type.code), onChange: function (e) {
                    return list.handleAuthorityTypeFilterChange(type.code, e.target.checked);
                } })); })),
        React.createElement(Accordion, { label: intl.formatMessage({
                id: "ui-czna-users.users.filter.organization",
            }), separator: false },
            organizationsError ? (React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-users.users.filter.organization.loadError" }))) : null,
            React.createElement(Selection, { id: "users-organization-filter", 
                // The accordion heading names the filter; the label is for screen readers only.
                label: React.createElement("span", { className: "sr-only" },
                    React.createElement(FormattedMessage, { id: "ui-czna-users.users.filter.organization" })), dataOptions: organizationOptions, value: list.organizationFilter, onChange: list.handleOrganizationFilterChange, onFilter: filterOrganizationOptions, loading: organizationsLoading, placeholder: intl.formatMessage({
                    id: "ui-czna-users.users.filter.organization.search",
                }), marginBottom0: true }))));
    var detailPane = selectedId ? (React.createElement(UserDetailPane, { id: selectedId, onClose: goToList, onEdit: goToEdit })) : undefined;
    return (React.createElement(SearchLayout, { list: list, searchPlaceholder: intl.formatMessage({
            id: "ui-czna-users.users.search.placeholder",
        }), paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-users.users.title" }), filterContent: filterContent, detailPane: detailPane, lastMenu: canCreateUser ? (React.createElement(PaneMenu, null,
            React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, onClick: goToCreate },
                React.createElement(Icon, { icon: "plus-sign" },
                    React.createElement(FormattedMessage, { id: "ui-czna-users.users.new" }))))) : undefined }, list.isLoading && list.items.length === 0 ? (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
        React.createElement(Layout, { className: "padding-all-gutter" },
            React.createElement(Loading, { size: "xlarge" })))) : list.items.length === 0 && !list.isLoading ? (list.isDefaultFilters ? (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
        React.createElement(Layout, { className: "padding-all-gutter" },
            React.createElement(Headline, { size: "medium", margin: "none" },
                React.createElement(FormattedMessage, { id: "ui-czna-users.users.empty.heading" })),
            React.createElement("p", { style: { color: "#666", marginTop: "8px" } },
                React.createElement(FormattedMessage, { id: "ui-czna-users.users.empty.subtext" })),
            canCreateUser && (React.createElement(Button, { buttonStyle: "primary", onClick: goToCreate, style: { marginTop: "16px" } },
                React.createElement(FormattedMessage, { id: "ui-czna-users.users.new" })))))) : (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
        React.createElement(Layout, { className: "padding-all-gutter" },
            React.createElement("p", { style: { color: "#666" } },
                React.createElement(FormattedMessage, { id: "ui-czna-users.users.noResults" })),
            React.createElement(Button, { onClick: list.handleClearFilters, style: { marginTop: "16px" } },
                React.createElement(FormattedMessage, { id: "ui-czna-users.users.clearFilters" })))))) : (
    /* @ts-expect-error - stripes MCL types require all entity keys but only visible columns are needed */
    React.createElement(MultiColumnList, { autosize: true, virtualize: true, loading: list.isFetching, contentData: list.items, totalCount: list.totalRecords, pagingType: "prev-next", pagingOffset: list.pageOffset, pageAmount: list.pageSize, columnMapping: {
            name: React.createElement(FormattedMessage, { id: "ui-czna-users.users.name" }),
            email: React.createElement(FormattedMessage, { id: "ui-czna-users.users.email" }),
            organization: (React.createElement(FormattedMessage, { id: "ui-czna-users.users.organization" })),
            status: React.createElement(FormattedMessage, { id: "ui-czna-users.users.status" }),
            roles: React.createElement(FormattedMessage, { id: "ui-czna-users.users.roles" }),
            createdAt: React.createElement(FormattedMessage, { id: "ui-czna-users.users.createdAt" }),
        }, visibleColumns: [
            "name",
            "email",
            "organization",
            "status",
            "roles",
            "createdAt",
        ], formatter: resultsFormatter, sortedColumn: list.sortColumn, sortDirection: list.sortDirection, onHeaderClick: list.handleSort, nonInteractiveHeaders: ["roles"], selectedRow: selectedId ? { id: selectedId } : undefined, onRowClick: handleRowClick, onNeedMoreData: function (_amount, offset) {
            return list.fetchMoreData(offset);
        } }))));
};
export default UsersList;
